import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Github, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Separator } from '@/components/ui/separator';
import {
  SITE_DESCRIPTION,
  SOCIAL_LINKS,
  PRIMARY_NAV,
  FOOTER_LEGAL_LINKS,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  OFFICE_ADDRESS,
} from '@/constants/site';
import { SERVICES_DATA } from '@/constants/services.data';
import { useSiteSettings } from '@/services/settings.service';

const FEATURED_SERVICES = SERVICES_DATA.slice(0, 6);

export function Footer() {
  // `constants/site.ts` values are the fallback whenever admin-managed
  // settings haven't loaded yet (or the request fails) — the footer renders
  // on every page and must never block or show blank contact info.
  const { data: settings } = useSiteSettings();
  const contactEmail = settings?.contactEmail || CONTACT_EMAIL;
  const contactPhone = settings?.phone || CONTACT_PHONE;
  const officeAddress = settings?.address || OFFICE_ADDRESS;
  const socialLinks = settings?.socialLinks ?? SOCIAL_LINKS;

  const SOCIALS = [
    { icon: Linkedin, href: socialLinks.linkedin, label: 'LinkedIn' },
    { icon: Twitter, href: socialLinks.twitter, label: 'Twitter / X' },
    { icon: Github, href: socialLinks.github, label: 'GitHub' },
    { icon: Youtube, href: socialLinks.youtube, label: 'YouTube' },
  ].filter((social) => social.href);

  return (
    <footer className="bg-dark border-t border-white/10 text-white/70">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-6">
          <div className="col-span-2">
            <Logo className="[&_span]:text-white" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed">{SITE_DESCRIPTION}</p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={social.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-white">
              Services
            </p>
            <ul className="space-y-2.5 text-sm">
              {FEATURED_SERVICES.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="transition-colors hover:text-white"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services"
                  className="text-primary font-medium transition-colors hover:text-white"
                >
                  View all →
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-white">Company</p>
            <ul className="space-y-2.5 text-sm">
              {PRIMARY_NAV.filter((l) => l.href !== '/services').map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="transition-colors hover:text-white">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/contact" className="transition-colors hover:text-white">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="col-span-2">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-white">
              Get in touch
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                <a href={`mailto:${contactEmail}`} className="transition-colors hover:text-white">
                  {contactEmail}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0" />
                <a
                  href={`tel:${contactPhone.replace(/[^+\d]/g, '')}`}
                  className="transition-colors hover:text-white"
                >
                  {contactPhone}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{officeAddress}</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-10 bg-white/10" />

        <div className="flex flex-col items-center justify-between gap-4 text-sm sm:flex-row">
          <p>© {new Date().getFullYear()} VEGAAHI IT PVT LTD. All rights reserved.</p>
          <div className="flex gap-6">
            {FOOTER_LEGAL_LINKS.map((link) => (
              <Link key={link.href} to={link.href} className="transition-colors hover:text-white">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
